import { useForm } from "react-hook-form";
import usePartAspectsMutations from "../../data/usePartAspectsMutations";
import usePartDefaultCategoryAssignees from "../../data/usePartDefaultCategoryAssignees";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import { RadioGroupInput } from "../shared/Inputs/RadioGroupInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { IPartAspect } from './../../models/EbayOffer';
import { Assignee, IAspectAssignee } from "../../models/Part";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";

const assigneeOptions = [
  { name: "None", value: Assignee.None },
  { name: "Imager", value: Assignee.Imager },
  { name: "Dismantler", value: Assignee.Dismantler }
];

interface newAspectFormValues {
  id: string | null;
  partCategoryDefaultsId: string | null;
  name: string;
  options: string | null;
  isRequired: boolean;
}

interface IAssigneeFormValues {
  assignee: Assignee;
}

export default function SingleCategoryCustomAspect({ aspect, aspectAssignees }: { aspect: IPartAspect, aspectAssignees: IAspectAssignee[] | undefined }) {
  const { createUpdateCategoryDefaults, remove } = usePartAspectsMutations();
  const { addAssignee } = usePartDefaultCategoryAssignees();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Category Default Aspect");
  const { saveHandlers: assigneeSaveHandlers } = useDefaultCRUDHandlers("Default Assignee");

  const { register, reset, handleSubmit } = useForm<newAspectFormValues>({
    defaultValues: {
      ...aspect,
    }
  });

  // Kept apart from the aspect form so the toggle saves on its own, like the eBay aspect rows.
  const { register: registerAssignee, setValue: setAssigneeValue, watch: watchAssignee } = useForm<IAssigneeFormValues>({
    defaultValues: {
      assignee: aspectAssignees?.find((a) => a.aspectName === aspect.name)?.assignee ?? Assignee.None
    }
  });

  const onSubmit = (data: newAspectFormValues) => {
    const newAspect = {
      ...data,
    };

    createUpdateCategoryDefaults.mutate(newAspect, {
      ...saveHandlers,
      onSuccess: () => {
        reset(data);
      }
    });
  }

  const handleAssigneeChange = (registerName: string, value: Assignee) => {
    const body = {
      defaultCategoryId: aspect.partCategoryDefaultsId,
      aspectName: aspect.name,
      assignee: value,
      imageUrl: null,
      isImageRequired: false
    };

    addAssignee.mutate(body, {
      onSuccess: () => {
        setAssigneeValue("assignee", value);
        assigneeSaveHandlers.onSuccess();
      },
      onError: () => {
        assigneeSaveHandlers.onError();
      }
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} key={aspect.id} className="grid grid-cols-5 gap-x-4 items-center border-0 border-b pb-4">
      <div className="flex items-center">
        <label className="font-medium text-gray-500">{aspect.name}</label>
      </div>

      <TextInput
        label="Options (comma separated)"
        register={register}
        registerName={`options`}
      />

      <CheckboxInput
        label="Required?"
        register={register}
        registerName={"isRequired"}
      />

      <RadioGroupInput
        options={assigneeOptions}
        register={registerAssignee}
        registerName={"assignee"}
        setValue={handleAssigneeChange}
        watch={watchAssignee}
      />

      <div>
        <ButtonInput
          label="Update"
          isSubmit
          onClick={undefined}
          classes={undefined}
          isLoading={createUpdateCategoryDefaults.isLoading}
        />

        <ButtonInput
          label="Delete"
          onClick={() => remove.mutate(aspect.id, deleteHandlers)}
          classes="ml-2 bg-red-500 hover:bg-red-600"
          isLoading={remove.isLoading}
        />
      </div>
    </form>
  )
}
